import * as Routes from '@/modules/routes';

// The sysadmin app tree, at rest. `to` is the SPA route; `href` a legacy page. `legacyHref` is
// where the item goes from a legacy page (the content rail's at-rest tree), when that differs
// from `/sysadmin` + `to` — the content tabs are mounted on /admin/programs as well as in the SPA.
export interface NavLeaf {
  href?: string;
  label: string;
  legacyHref?: string;
  // Nested one step under the item before it (Custom Programs under Programs).
  sub?: boolean;
  to?: string;
}

export type NavAccess = 'content' | 'support';

export interface NavSection {
  // The role capability a section needs; unrestricted sections show to every sysadmin role.
  access?: NavAccess;
  items: NavLeaf[];
  label: string;
}

export interface NavAccessFlags {
  canManageContent: boolean;
  canManageSupport: boolean;
}

export const visibleNavSections = (sections: NavSection[], flags: NavAccessFlags) => sections.filter((section) => {
  if (section.access === 'content') return flags.canManageContent;
  if (section.access === 'support') return flags.canManageSupport;

  return true;
});

const navSections = (supportPath: string): NavSection[] => [
  {
    items: [
      { label: 'Programs', legacyHref: Routes.admin_programs_path(), to: '/content' },
      {
        label: 'Custom Programs',
        legacyHref: `${Routes.admin_programs_path()}/custom-programs`,
        sub: true,
        to: '/content/custom-programs',
      },
      {
        label: 'Reference Materials',
        legacyHref: `${Routes.admin_programs_path()}/reference-materials`,
        to: '/content/reference-materials',
      },
      {
        label: 'Alert Messages',
        legacyHref: `${Routes.admin_programs_path()}/alert-messages`,
        to: '/content/alert-messages',
      },
    ],
    access: 'content',
    label: 'Content',
  },
  {
    items: [
      { label: 'User Search', legacyHref: Routes.admin_user_search_index_path(), to: '/support/user-search' },
      { label: 'Grade Sync', to: '/support/grade-sync' },
      { label: 'Troubleshoot a sync', sub: true, to: '/support/grade-sync/troubleshoot' },
      { label: 'Platform health', sub: true, to: '/support/grade-sync/platform' },
      { label: 'Rate limits', sub: true, to: '/support/grade-sync/rate-limits' },
      { href: supportPath, label: 'Subscribers' },
      { href: Routes.admin_search_index_path(), label: 'Teachers' },
      { href: Routes.search_admin_search_purchase_orders_path(), label: 'Purchase Orders' },
      { href: Routes.search_admin_invitations_path(), label: 'Invitations' },
      { href: Routes.admin_system_messages_path(), label: 'Sys Messages' },
    ],
    access: 'support',
    label: 'Support',
  },
  {
    items: [
      { label: 'DevDocs', to: '/dev/dev-docs' },
      { label: 'Content Audit', to: '/dev/content-audit' },
      { label: 'AI Safety', to: '/dev/ai-safety' },
      { label: 'AI Policy', to: '/dev/ai-policy' },
      { label: 'System Status', to: '/dev/system-status' },
      { href: '/sidekiq', label: 'Sidekiq' },
      { href: '/pghero', label: 'PgHero' },
    ],
    label: 'Dev',
  },
  {
    items: [
      { label: 'Langfuse', to: '/langfuse' },
      { label: 'Prompt Management', sub: true, to: '/langfuse/prompts' },
      { label: 'Trace Viewer', sub: true, to: '/langfuse/traces' },
    ],
    label: 'Elsewhere',
  },
];

export default navSections;
